import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { SYNC_MODULES, type SyncModule, formatDateTime } from "@nexseed/shared";
import { Progress } from "@/components/ui/progress";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Database,
  Download,
  Package,
  RefreshCw,
  Send,
  Truck,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/empty-state";
import { PageHeader } from "@/components/app/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useAuth } from "@/features/auth/store";
import { api, ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/integrations")({
  component: Integrations,
});

interface RunResult {
  counts: Record<string, number>;
  error: string | null;
}
interface SyncRow {
  module: string;
  lastManualAt: string | null;
  lastManualStatus: string | null;
  lastManualResult: RunResult | null;
  lastAutoAt: string | null;
  lastAutoStatus: string | null;
  lastAutoResult: RunResult | null;
}
interface Status {
  coreStub: boolean;
  lotgenStub: boolean;
  syncState: SyncRow[];
  outbox: { pending: number; failed: number; sent: number };
  scapture: { waiting: number; newestAt: string | null };
  /** COB trucks on S-Capture's cob feed that no live Cob Arrival carries yet. */
  scaptureCob: { waiting: number; newestAt: string | null };
}
interface OutboxEvent {
  id: number;
  event: string;
  status: string;
  attempts: number;
  nextAttemptAt: string;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string;
}

// Mirrors SCHEDULES in apps/api/src/jobs/queue.ts: masters nightly 02:00-02:30, both S-Capture feeds
// every 15 min.
const DAY_MS = 24 * 60 * 60 * 1000;
const MASTER_AUTO_GRACE_MS = DAY_MS + 2 * 60 * 60 * 1000;
const SCAPTURE_GRACE_MS = 45 * 60 * 1000;

const ago = (iso: string) =>
  formatDistanceToNow(new Date(iso), { addSuffix: true });
const when = (iso: string) => formatDateTime(iso);
const fmt = (n: number | undefined) =>
  n === undefined ? "—" : n.toLocaleString("en-IN");

/** Whichever of the manual and automatic runs happened last. */
function latestRun(r: SyncRow | undefined) {
  if (!r) return null;
  const manual = r.lastManualAt ? Date.parse(r.lastManualAt) : 0;
  const auto = r.lastAutoAt ? Date.parse(r.lastAutoAt) : 0;
  if (!manual && !auto) return null;
  return manual >= auto
    ? {
        at: r.lastManualAt!,
        status: r.lastManualStatus,
        result: r.lastManualResult,
        how: "manual",
      }
    : {
        at: r.lastAutoAt!,
        status: r.lastAutoStatus,
        result: r.lastAutoResult,
        how: "auto",
      };
}

const autoOverdue = (r: SyncRow | undefined, graceMs: number, now: number) =>
  !r?.lastAutoAt || now - Date.parse(r.lastAutoAt) > graceMs;

function Integrations() {
  const qc = useQueryClient();
  const canManage = useAuth((s) => s.can)("integrations.manage");
  const status = useQuery({
    queryKey: ["integrations", "status"],
    queryFn: () => api<Status>("/integrations/status"),
    refetchInterval: 15_000,
  });
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["integrations", "status"] });

  const [syncing, setSyncing] = useState<SyncModule | "all" | null>(null);
  // "Sync all" is five sequential calls, each of which can sit on a 15s upstream timeout — well over
  // a minute of one spinner. The loop index is a real denominator, so the operator gets a real bar
  // and the name of the module being pulled rather than a guess about whether it has hung.
  const [step, setStep] = useState<{ done: number; total: number } | null>(
    null
  );
  const syncOne = async (module: SyncModule) => {
    const r = await api<{ status: string; error?: string }>(
      `/integrations/core/sync/${module}`,
      { method: "POST", json: {} }
    );
    if (r.status === "failed")
      throw new Error(`${module}: ${r.error ?? "failed"}`);
  };
  const sync = useMutation({
    mutationFn: async (target: SyncModule | "all") => {
      setSyncing(target);
      // In SYNC_MODULES order: varieties resolve against classifications, so order matters.
      const modules = target === "all" ? SYNC_MODULES : [target];
      setStep({ done: 0, total: modules.length });
      for (const [i, m] of modules.entries()) {
        setStep({ done: i, total: modules.length });
        await syncOne(m);
      }
      setStep({ done: modules.length, total: modules.length });
    },
    onSuccess: (_r, target) =>
      toast.success(
        target === "all"
          ? "All masters pulled from VNR Core"
          : `${target} pulled from VNR Core`
      ),
    onError: (e) =>
      toast.error(
        e instanceof ApiError || e instanceof Error ? e.message : "Sync failed"
      ),
    onSettled: () => {
      setSyncing(null);
      setStep(null);
      void refresh();
    },
  });
  const pull = useMutation({
    mutationFn: () =>
      api<{ created: number; found: number[] }>("/integrations/scapture/pull", {
        method: "POST",
        json: {},
      }),
    onSuccess: (r) =>
      toast.success(
        `S-Capture: ${r.found.length} dispatches, ${r.created} new lots`
      ),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "S-Capture pull failed"),
    onSettled: () => void refresh(),
  });
  const pullCob = useMutation({
    mutationFn: () =>
      api<{ trucks: number; created: number }>(
        "/integrations/scapture-cob/pull",
        { method: "POST", json: {} }
      ),
    onSuccess: (r) =>
      toast.success(`S-Capture COB: ${r.trucks} trucks, ${r.created} new lots`),
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : "S-Capture COB pull failed"
      ),
    onSettled: () => void refresh(),
  });
  const [eventsOpen, setEventsOpen] = useState(false);

  const data = status.data;
  // Staleness is judged against when this status was fetched, which keeps render pure; the 15 s
  // refetch moves it forward.
  const now = status.dataUpdatedAt;
  const byModule = new Map(data?.syncState.map((r) => [r.module, r]));
  const masters = SYNC_MODULES.map((m) => ({
    module: m,
    row: byModule.get(m),
    last: latestRun(byModule.get(m)),
  }));
  const scaptureRow = byModule.get("scapture");
  const lastPull = latestRun(scaptureRow);
  const lastCobPull = latestRun(byModule.get("scapture_cob"));

  // What needs a person. Nothing listed means the strip is not drawn at all.
  const attention: string[] = [];
  if (data) {
    const overdue = masters.filter((m) =>
      autoOverdue(m.row, MASTER_AUTO_GRACE_MS, now)
    );
    if (overdue.length) {
      const lastAuto = masters
        .map((m) => m.row?.lastAutoAt)
        .filter((x): x is string => !!x)
        .sort()
        .at(-1);
      attention.push(
        lastAuto
          ? `VNR Core: no automatic sync recorded in this database since ${when(lastAuto)} (${overdue.length} of ${masters.length} masters).`
          : "VNR Core: no automatic sync has ever been recorded in this database."
      );
    }
    for (const m of masters)
      if (m.last && m.last.status !== "success")
        attention.push(
          `VNR Core ${m.module}: last sync ${m.last.status}${m.last.result?.error ? `: ${m.last.result.error}` : ""}.`
        );
    if (lastPull?.status === "failed")
      attention.push(
        `S-Capture: last pull failed${lastPull.result?.error ? `: ${lastPull.result.error}` : ""}.`
      );
    else if (!lastPull || now - Date.parse(lastPull.at) > SCAPTURE_GRACE_MS)
      attention.push(
        lastPull
          ? `S-Capture: no pull recorded since ${when(lastPull.at)}.`
          : "S-Capture: no pull has been recorded in this database yet."
      );
    if (lastCobPull?.status === "failed")
      attention.push(
        `S-Capture COB: last pull failed${lastCobPull.result?.error ? `: ${lastCobPull.result.error}` : ""}.`
      );
    else if (
      !lastCobPull ||
      now - Date.parse(lastCobPull.at) > SCAPTURE_GRACE_MS
    )
      attention.push(
        lastCobPull
          ? `S-Capture COB: no pull recorded since ${when(lastCobPull.at)}.`
          : "S-Capture COB: no pull has been recorded in this database yet."
      );
    if (data.outbox.failed)
      attention.push(
        `Core outbox: ${data.outbox.failed} event${data.outbox.failed === 1 ? "" : "s"} failed and stopped retrying.`
      );
  }

  const mastersFailing = masters.some(
    (m) => m.last && m.last.status !== "success"
  );
  const mastersOverdue = masters.some((m) =>
    autoOverdue(m.row, MASTER_AUTO_GRACE_MS, now)
  );
  const pullBadgeFor = (run: ReturnType<typeof latestRun>) =>
    run?.status === "failed" ? (
      <Badge tone="danger" dot>
        Failing
      </Badge>
    ) : !run ? (
      <Badge tone="neutral" dot>
        No pulls yet
      </Badge>
    ) : now - Date.parse(run.at) > SCAPTURE_GRACE_MS ? (
      <Badge tone="warning" dot>
        Overdue
      </Badge>
    ) : (
      <Badge tone="success" dot>
        Healthy
      </Badge>
    );

  return (
    <>
      <PageHeader
        title="Integrations"
        description="Every outside system NexSeed talks to, whether it is healthy, and what to do if it is not."
      />

      {attention.length > 0 && (
        <Callout
          tone="warning"
          className="mb-4"
          title={`${attention.length} ${attention.length === 1 ? "needs" : "need"} attention`}
          action={
            canManage && (mastersOverdue || mastersFailing) ? (
              <Button
                size="sm"
                variant="secondary"
                loading={syncing === "all"}
                disabled={sync.isPending}
                onClick={() => sync.mutate("all")}
              >
                {syncing !== "all" && <RefreshCw className="h-4 w-4" />} Sync
                all masters
              </Button>
            ) : undefined
          }
        >
          <ul className="mt-0.5 space-y-0.5">
            {attention.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Callout>
      )}

      {status.isError && (
        <Callout
          tone="danger"
          className="mb-4"
          title="Could not load integration status"
        >
          {status.error instanceof ApiError
            ? status.error.message
            : "Try again in a moment."}
        </Callout>
      )}

      <div className="grid items-stretch gap-4 md:grid-cols-2">
        <SystemCard
          icon={Database}
          name="VNR Core"
          what="Owns crops, varieties, vendors and the other masters. Read-only here."
          badges={
            data && (
              <>
                <Badge tone={data.coreStub ? "warning" : "success"} dot>
                  {data.coreStub ? "Stub" : "Live"}
                </Badge>
                {mastersFailing ? (
                  <Badge tone="danger">Sync failed</Badge>
                ) : mastersOverdue ? (
                  <Badge tone="warning">Auto sync overdue</Badge>
                ) : null}
              </>
            )
          }
          footerNote="Nightly 2:00–2:30 am"
          actions={
            canManage && (
              <Button
                size="sm"
                variant="secondary"
                loading={syncing === "all"}
                disabled={sync.isPending}
                onClick={() => sync.mutate("all")}
              >
                {syncing !== "all" && <RefreshCw className="h-4 w-4" />} Sync
                all
              </Button>
            )
          }
        >
          {syncing === "all" && step && (
            <div className="mb-3">
              <Progress
                value={step.done / step.total}
                label="Pulling masters from VNR Core"
                thick
              />
              <p className="mt-1.5 text-xs text-ink-muted">
                Pulling {step.done + 1} of {step.total} ·{" "}
                {SYNC_MODULES[Math.min(step.done, step.total - 1)]}
              </p>
            </div>
          )}
          <div className="overflow-hidden rounded-md border border-border">
            {masters.map(({ module, last }, i) => {
              const failed = !!last && last.status !== "success";
              return (
                <div
                  key={module}
                  className={cn(
                    "flex items-center gap-3 px-3 py-1.5 text-compact",
                    i > 0 && "border-t border-border"
                  )}
                >
                  <span className="w-28 font-medium capitalize text-ink">
                    {module}
                  </span>
                  <span className="num w-16 text-right text-ink-secondary">
                    {fmt(last?.result?.counts.fetched)}
                  </span>
                  <span
                    className="min-w-0 flex-1 truncate text-ink-muted"
                    title={last ? when(last.at) : undefined}
                  >
                    {last ? `${ago(last.at)}, ${last.how}` : "Never synced"}
                  </span>
                  {last &&
                    (failed ? (
                      <Badge
                        tone="danger"
                        variant="pill"
                        className="shrink-0"
                        title="Last sync failed"
                      >
                        <XCircle className="h-3.5 w-3.5" aria-hidden />
                        Failed
                      </Badge>
                    ) : (
                      <Badge
                        tone="good"
                        variant="pill"
                        className="shrink-0"
                        title="Last sync succeeded"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                        OK
                      </Badge>
                    ))}
                  {canManage && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label={`Sync ${module} now`}
                      loading={syncing === module}
                      disabled={sync.isPending}
                      onClick={() => sync.mutate(module)}
                    >
                      {syncing !== module && (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </SystemCard>

        <SystemCard
          icon={Truck}
          name="S-Capture"
          what="Dispatch feed. Each new truck becomes lots waiting on the Pending tab."
          badges={data && pullBadgeFor(lastPull)}
          footerNote="Every 15 minutes"
          actions={
            canManage && (
              <Button
                size="sm"
                variant="secondary"
                loading={pull.isPending}
                onClick={() => pull.mutate()}
              >
                {!pull.isPending && <Download className="h-4 w-4" />} Pull now
              </Button>
            )
          }
        >
          <div className="flex items-baseline gap-2">
            <span className="num text-3xl font-semibold text-ink">
              {fmt(data?.scapture.waiting)}
            </span>
            <span className="text-compact text-ink-muted">
              lots awaiting arrival
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-[8.5rem_1fr] gap-y-1.5 text-compact">
            <dt className="text-ink-muted">Last pull</dt>
            <dd
              className={cn(
                lastPull?.status === "failed" && "text-danger-text"
              )}
            >
              {!lastPull
                ? "None recorded yet"
                : lastPull.status === "failed"
                  ? `${ago(lastPull.at)}, failed: ${lastPull.result?.error ?? "unknown error"}`
                  : `${ago(lastPull.at)} · ${fmt(lastPull.result?.counts.dispatches)} dispatches, ${fmt(lastPull.result?.counts.created)} new lots`}
            </dd>
            <dt className="text-ink-muted">Newest lot</dt>
            <dd>
              {data?.scapture.newestAt ? when(data.scapture.newestAt) : "—"}
            </dd>
            <dt className="text-ink-muted">Moisture rejected</dt>
            <dd>
              {lastPull?.status === "success"
                ? `${fmt(lastPull.result?.counts.moistureRejected)} in last pull`
                : "—"}
            </dd>
          </dl>
        </SystemCard>

        <SystemCard
          icon={Truck}
          name="S-Capture COB"
          what="Maize cob trucks. One for the dryer is picked on COB Arrival; one for our plant is let in at its gate and received as Maize-Dried Seed Arrival."
          badges={data && pullBadgeFor(lastCobPull)}
          footerNote="Every 15 minutes"
          actions={
            canManage && (
              <Button
                size="sm"
                variant="secondary"
                loading={pullCob.isPending}
                onClick={() => pullCob.mutate()}
              >
                {!pullCob.isPending && <Download className="h-4 w-4" />} Pull
                now
              </Button>
            )
          }
        >
          <div className="flex items-baseline gap-2">
            <span className="num text-3xl font-semibold text-ink">
              {fmt(data?.scaptureCob.waiting)}
            </span>
            <span className="text-compact text-ink-muted">
              trucks awaiting COB Arrival
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-[8.5rem_1fr] gap-y-1.5 text-compact">
            <dt className="text-ink-muted">Last pull</dt>
            <dd
              className={cn(
                lastCobPull?.status === "failed" && "text-danger-text"
              )}
            >
              {!lastCobPull
                ? "None recorded yet"
                : lastCobPull.status === "failed"
                  ? `${ago(lastCobPull.at)}, failed: ${lastCobPull.result?.error ?? "unknown error"}`
                  : `${ago(lastCobPull.at)} · ${fmt(lastCobPull.result?.counts.trucks)} trucks, ${fmt(lastCobPull.result?.counts.created)} new lots`}
            </dd>
            <dt className="text-ink-muted">Newest truck</dt>
            <dd>
              {data?.scaptureCob.newestAt
                ? when(data.scaptureCob.newestAt)
                : "—"}
            </dd>
          </dl>
        </SystemCard>

        <SystemCard
          icon={Package}
          name="Lotgen"
          what="Lot catalogue the arrival picker reads on demand. Nothing is stored here."
          badges={
            data && (
              <Badge tone={data.lotgenStub ? "warning" : "success"} dot>
                {data.lotgenStub ? "Stub" : "Live"}
              </Badge>
            )
          }
          footerNote="On demand, no schedule"
        >
          {data?.lotgenStub ? (
            <div className="flex gap-2.5 rounded-md bg-surface-sunken px-3 py-2.5 text-compact text-ink-secondary">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                aria-hidden
              />
              <span>
                Running on sample lots. Arrival pickers will not show real
                Lotgen lots until the Lotgen API key is configured on the
                server.
              </span>
            </div>
          ) : (
            <p className="text-compact text-ink-muted">
              Arrival pickers read the live Lotgen catalogue each time they
              open.
            </p>
          )}
        </SystemCard>

        <SystemCard
          icon={Send}
          name="Core outbox"
          what="Arrival Done and its reversal, pushed back to VNR Core with retries."
          badges={
            data &&
            (data.outbox.failed ? (
              <Badge tone="danger" dot>
                {data.outbox.failed} failed
              </Badge>
            ) : (
              <Badge tone="success" dot>
                Healthy
              </Badge>
            ))
          }
          footerNote="Every minute, retried with backoff"
          actions={
            canManage && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEventsOpen(true)}
              >
                View events <ChevronRight className="h-4 w-4" />
              </Button>
            )
          }
        >
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                ["Pending", data?.outbox.pending, false],
                ["Failed", data?.outbox.failed, !!data?.outbox.failed],
                ["Sent", data?.outbox.sent, false],
              ] as const
            ).map(([label, n, bad]) => (
              <div
                key={label}
                className="rounded-md bg-surface-sunken px-3 py-2"
              >
                <div className="text-xs text-ink-muted">{label}</div>
                <div
                  className={cn(
                    "num text-lg font-semibold",
                    bad ? "text-danger-text" : "text-ink"
                  )}
                >
                  {fmt(n)}
                </div>
              </div>
            ))}
          </div>
          {data &&
            !data.outbox.pending &&
            !data.outbox.failed &&
            !data.outbox.sent && (
              <p className="mt-3 text-compact text-ink-muted">
                No events yet. A failed event shows its last error and when it
                retries next.
              </p>
            )}
        </SystemCard>
      </div>

      {canManage && (
        <OutboxEvents open={eventsOpen} onOpenChange={setEventsOpen} />
      )}
    </>
  );
}

function SystemCard({
  icon: Icon,
  name,
  what,
  badges,
  footerNote,
  actions,
  children,
}: {
  icon: LucideIcon;
  name: string;
  what: string;
  badges?: ReactNode;
  footerNote: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col overflow-hidden rounded-2xl shadow-card">
      <div className="flex items-start gap-3 px-4 pb-3 pt-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary-soft text-primary-text">
          <Icon className="h-4.5 w-4.5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lead font-semibold text-ink">{name}</h2>
            {badges}
          </div>
          <p className="text-compact text-ink-muted">{what}</p>
        </div>
      </div>
      <div className="flex-1 px-4 pb-4">{children}</div>
      <div className="flex min-h-13 items-center justify-between gap-2 border-t border-border bg-surface px-4 py-2.5">
        <span className="text-xs text-ink-muted">{footerNote}</span>
        {actions && <div className="flex gap-2">{actions}</div>}
      </div>
    </Card>
  );
}

/** Fetched only while the panel is open, so the page itself stays one request. */
function OutboxEvents({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const events = useQuery({
    queryKey: ["integrations", "outbox"],
    queryFn: () => api<OutboxEvent[]>("/integrations/outbox?limit=50"),
    enabled: open,
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        size="xl"
        title="Core outbox events"
        description="The 50 most recent events pushed to VNR Core."
      >
        {events.data && events.data.length === 0 ? (
          <EmptyState
            title="No events yet"
            description="Completing a PDN arrival queues one here."
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Event</TH>
                <TH>Status</TH>
                <TH align="right">Attempts</TH>
                <TH>Created</TH>
                <TH>Sent / next try</TH>
              </tr>
            </THead>
            <TBody>
              {events.data?.map((e) => (
                <TR key={e.id}>
                  <TD className="font-mono text-xs">{e.event}</TD>
                  <TD>
                    <Badge
                      tone={
                        e.status === "sent"
                          ? "success"
                          : e.status === "failed"
                            ? "danger"
                            : "neutral"
                      }
                    >
                      {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                    </Badge>
                    {e.lastError && (
                      <div
                        className="mt-1 max-w-64 truncate text-xs text-danger-text"
                        title={e.lastError}
                      >
                        {e.lastError}
                      </div>
                    )}
                  </TD>
                  <TD numeric>{e.attempts}</TD>
                  <TD className="whitespace-nowrap text-ink-muted">
                    {when(e.createdAt)}
                  </TD>
                  <TD className="whitespace-nowrap text-ink-muted">
                    {e.sentAt
                      ? when(e.sentAt)
                      : e.status === "pending"
                        ? when(e.nextAttemptAt)
                        : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </SheetContent>
    </Sheet>
  );
}
